import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { alsDatum, alsDauer, alsUhrzeit, api, wochentagKurz } from '../api/client'
import { alsGroesse, Dokumente } from '../komponenten/Dokumente'
import type { KursUebersicht } from './Kurse'

interface PauseAntwort {
  nummer: number
  von: number
  bis: number
  dauer: number
  anrechenbar: boolean
}

interface TagAntwort {
  wochentag: string
  dienstbeginn1: number | null
  dienstende1: number | null
  dienstbeginn2: number | null
  dienstende2: number | null
  arbeitszeitBerechnet: number | null
  arbeitszeitBlatt: number | null
  abweichung: number | null
  pausen: PauseAntwort[]
}

interface ElementAntwort {
  reihenfolge: number
  typ: string
  ab: number | null
  an: number | null
  dauer: number | null
  linie: string | null
  umlauf: string | null
  von: string | null
  nach: string | null
  einsatztage: string | null
  freitext: string | null
}

interface TeilAntwort {
  nummer: number
  ausfahrtArt: string | null
  einfahrtArt: string | null
  fahrzeuggruppe: string | null
  beginn: number | null
  ende: number | null
  elemente: ElementAntwort[]
}

/** Eine zum Kurs gehoerende Datei — Kursblatt, Anhang des Blatts oder Anhang des Kurses. */
interface DokumentVerweis {
  id: number
  herkunft: 'FAHRERMAPPE' | 'KURSBLATT' | 'BLATT_ANHANG' | 'KURS_ANHANG'
  art: string
  dateiname: string
  mimetyp: string | null
  groesseBytes: number
  url: string
  loeschbar: boolean
}

interface KursDetailAntwort {
  kopf: KursUebersicht
  tage: TagAntwort[]
  teile: TeilAntwort[]
  dokumente: DokumentVerweis[]
}

const HERKUNFT_TEXT: Record<string, string> = {
  FAHRERMAPPE: 'Fahrermappe — Seite 1 der Kurs, Seite 2 der Fahrplan',
  KURSBLATT: 'Kursblatt, aus dem dieser Kurs stammt',
  BLATT_ANHANG: 'am Kursblatt hinterlegt',
  KURS_ANHANG: 'an diesem Kurs hinterlegt',
}

const TYP_BESCHRIFTUNG: Record<string, string> = {
  LINIENFAHRT: 'Linienfahrt',
  LEERFAHRT: 'Leerfahrt',
  AUSFAHRT: 'Ausfahrt',
  EINFAHRT: 'Einfahrt',
  ABLOESUNG: 'Ablösung',
  WENDEZEIT: 'Wendezeit',
}

export function KursDetail() {
  const { id } = useParams()
  const [daten, setDaten] = useState<KursDetailAntwort | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      setDaten(await api.get<KursDetailAntwort>(`/api/kurse/${id}`))
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen')
    }
  }, [id])

  useEffect(() => {
    void laden()
  }, [laden])

  async function holen(dokument: DokumentVerweis) {
    try {
      await api.herunterladen(dokument.url, dokument.dateiname)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Herunterladen fehlgeschlagen')
    }
  }

  if (fehler) return <div className="meldung fehler">{fehler}</div>
  if (!daten) return <div className="laden">Wird geladen …</div>

  const { kopf, tage, teile, dokumente } = daten

  // Zeitfenster des Tages für die Bandbreite der grafischen Darstellung
  const alleZeiten = teile
    .flatMap((t) => t.elemente.flatMap((e) => [e.ab, e.an]))
    .filter((z): z is number => z !== null)
  const fensterStart = alleZeiten.length ? Math.min(...alleZeiten) - 20 : 0
  const fensterEnde = alleZeiten.length ? Math.max(...alleZeiten) + 20 : 1440
  const fensterBreite = Math.max(fensterEnde - fensterStart, 1)

  const anteil = (wert: number) => ((wert - fensterStart) / fensterBreite) * 100

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Kurs {kopf.kursnummer}</h1>
          <p>
            {kopf.verkehrstagtypText ?? kopf.verkehrstagtyp} · gültig ab {alsDatum(kopf.gueltigAb)}
            {kopf.nurAnEinemTag && ' (nur an diesem Tag)'}
            {kopf.quellblatt && ` · Blatt „${kopf.quellblatt}“`}
          </p>
        </div>
        <Link to="/kurse">
          <button className="knopf-schlicht">Zurück zur Liste</button>
        </Link>
      </div>

      {kopf.hatAbweichung && (
        <div className="meldung hinweis">
          Die im Kursblatt eingetragene Arbeitszeit weicht von der Berechnung ab. Maßgeblich ist der
          berechnete Wert (Dienstdauer abzüglich der eingetragenen Pausen).
        </div>
      )}

      <div className="abschnitt">
        <h2>Dienstzeiten je Wochentag</h2>
        <div className="tabelle-huelle">
          <table>
            <thead>
              <tr>
                <th>Tag</th>
                <th>Dienstteil 1</th>
                <th>Dienstteil 2</th>
                <th>Pausen</th>
                <th>Arbeitszeit</th>
                <th>Laut Blatt</th>
              </tr>
            </thead>
            <tbody>
              {tage.map((tag) => (
                <tr key={tag.wochentag}>
                  <td>
                    <strong>{wochentagKurz(tag.wochentag)}</strong>
                  </td>
                  <td className="zahl">
                    {tag.dienstbeginn1 !== null
                      ? `${alsUhrzeit(tag.dienstbeginn1)} – ${alsUhrzeit(tag.dienstende1)}`
                      : '–'}
                  </td>
                  <td className="zahl">
                    {tag.dienstbeginn2 !== null
                      ? `${alsUhrzeit(tag.dienstbeginn2)} – ${alsUhrzeit(tag.dienstende2)}`
                      : '–'}
                  </td>
                  <td>
                    {tag.pausen.length === 0 ? (
                      <span style={{ color: 'var(--grau-500)' }}>keine</span>
                    ) : (
                      tag.pausen.map((p) => (
                        <span
                          key={p.nummer}
                          className={`etikett ${p.anrechenbar ? 'grau' : 'rot'}`}
                          style={{ marginRight: 5 }}
                          title={
                            p.anrechenbar
                              ? `${p.dauer} Minuten`
                              : `${p.dauer} Minuten — Teilpausen unter 15 Minuten sind nach ArbZG nicht anrechenbar`
                          }
                        >
                          {alsUhrzeit(p.von)}–{alsUhrzeit(p.bis)} ({p.dauer}′)
                        </span>
                      ))
                    )}
                  </td>
                  <td className="zahl">
                    <strong>{alsDauer(tag.arbeitszeitBerechnet)}</strong>
                  </td>
                  <td className="zahl">
                    {alsDauer(tag.arbeitszeitBlatt)}
                    {tag.abweichung !== null && tag.abweichung !== 0 && (
                      <span className="etikett rot" style={{ marginLeft: 6 }}>
                        {tag.abweichung > 0 ? '+' : ''}
                        {tag.abweichung}′
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {teile.map((teil) => (
        <div className="abschnitt" key={teil.nummer}>
          <h2>
            Dienstteil {teil.nummer}
            {teil.beginn !== null && (
              <span style={{ fontWeight: 400, color: 'var(--grau-500)', marginLeft: 10, fontSize: '0.9rem' }}>
                {alsUhrzeit(teil.beginn)} – {alsUhrzeit(teil.ende)}
                {teil.ausfahrtArt && ` · Ausfahrt ${teil.ausfahrtArt}`}
                {teil.einfahrtArt && ` · Einfahrt ${teil.einfahrtArt}`}
                {teil.fahrzeuggruppe && ` · FzGr ${teil.fahrzeuggruppe}`}
              </span>
            )}
          </h2>

          <div className="karte" style={{ marginBottom: 14 }}>
            <div className="dienstband">
              {teil.elemente
                .filter((e) => e.ab !== null && e.an !== null)
                .map((e) => (
                  <div
                    key={e.reihenfolge}
                    className={`dienstband-block ${e.typ.toLowerCase()}`}
                    style={{
                      left: `${anteil(e.ab!)}%`,
                      width: `${Math.max(((e.an! - e.ab!) / fensterBreite) * 100, 0.6)}%`,
                    }}
                    title={`${TYP_BESCHRIFTUNG[e.typ] ?? e.typ} ${alsUhrzeit(e.ab)}–${alsUhrzeit(e.an)}${
                      e.linie ? ` · Linie ${e.linie}` : ''
                    }`}
                  >
                    {e.linie ?? ''}
                  </div>
                ))}
            </div>
            <div className="legende">
              <span>
                <i style={{ background: 'var(--navy-700)' }} /> Linienfahrt
              </span>
              <span>
                <i style={{ background: 'var(--grau-500)' }} /> Leerfahrt
              </span>
              <span>
                <i style={{ background: 'var(--navy-500)' }} /> Aus-/Einfahrt
              </span>
              <span>
                <i style={{ background: 'var(--rot-500)' }} /> Ablösung
              </span>
            </div>
          </div>

          <div className="tabelle-huelle">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Ab</th>
                  <th>An</th>
                  <th>Dauer</th>
                  <th>Art</th>
                  <th>Linie</th>
                  <th>Umlauf</th>
                  <th>Von</th>
                  <th>Nach</th>
                  <th>Tage</th>
                </tr>
              </thead>
              <tbody>
                {teil.elemente.map((e) => (
                  <tr key={e.reihenfolge}>
                    <td className="zahl">{e.reihenfolge}</td>
                    <td className="zahl">{alsUhrzeit(e.ab)}</td>
                    <td className="zahl">{alsUhrzeit(e.an)}</td>
                    <td className="zahl">{e.dauer !== null ? `${e.dauer}′` : '–'}</td>
                    <td>
                      <span
                        className={`etikett ${
                          e.typ === 'LINIENFAHRT'
                            ? 'navy'
                            : e.typ === 'ABLOESUNG'
                              ? 'rot'
                              : 'grau'
                        }`}
                      >
                        {TYP_BESCHRIFTUNG[e.typ] ?? e.typ}
                      </span>
                    </td>
                    <td>{e.linie ?? '–'}</td>
                    <td className="zahl">{e.umlauf ?? '–'}</td>
                    <td>{e.von ?? (e.freitext ?? '–')}</td>
                    <td>{e.nach ?? '–'}</td>
                    <td style={{ color: 'var(--grau-500)', fontSize: '0.82rem' }}>
                      {e.einsatztage ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Unterlagen zum Kurs. Das Kursblatt und die Anhaenge des Blatts kommen vom Server
          mit — sie gelten fuer jeden Kurs daraus und liegen deshalb nicht am Kurs selbst. */}
      <div className="abschnitt">
        <h2>Unterlagen</h2>
        <div className="tabelle-huelle">
          <table>
            <thead>
              <tr>
                <th>Datei</th>
                <th>Herkunft</th>
                <th>Größe</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dokumente.length === 0 ? (
                <tr>
                  <td colSpan={4} className="leer">
                    Zu diesem Kurs ist nichts hinterlegt.
                  </td>
                </tr>
              ) : (
                dokumente.map((d) => (
                  <tr key={`${d.herkunft}-${d.id}`}>
                    <td>
                      <strong>{d.dateiname}</strong>
                    </td>
                    <td style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
                      {HERKUNFT_TEXT[d.herkunft] ?? d.herkunft}
                    </td>
                    <td className="zahl">{alsGroesse(d.groesseBytes)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        className="knopf-schlicht"
                        style={{ padding: '5px 12px' }}
                        onClick={() => void holen(d)}
                      >
                        Herunterladen
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Dokumente
          kompakt
          bezugsart="KURS"
          bezugId={kopf.id}
          titel="Nur an diesem Kurs"
          hinweis="Was ausschließlich diesen einen Kurs betrifft. Der Fahrplan des Bestellers gehört an das Kursblatt in der Bibliothek — von dort gilt er für alle Kurse daraus."
          beiAenderung={() => void laden()}
        />
      </div>
    </>
  )
}
